import { RecognizeCriterionManagedBy } from "@redotech/crdb-enums/recognize/recognize-criterion-managed-by";

import { RecognizeCriterionEvaluator } from "@redotech/crdb-enums/recognize/recognize-criterion-evaluator";

/** Bumped when the classification harness changes such that stored answers are stale. */
export const CURRENT_CLASSIFIER_VERSION = 1;

/**
 * A backstop against runaway cost, not a product limit: each criterion adds one
 * LLM call per revealed shopper.
 */
export const MAX_MERCHANT_CRITERIA = 50;

/**
 * Keys of the criteria Redo ships and manages. A merchant's own criteria get
 * generated keys and are never members of this enum.
 */
export enum RedoCriterionKey {
  InvestorOrWealthManager = "investor_or_wealth_manager",
  AssistantToPublicFigure = "assistant_to_public_figure",
  WidelyKnownFigure = "widely_known_figure",
  IndustryExpert = "industry_expert",
  SeniorExecutive = "senior_executive",
  JournalistAtTopOutlet = "journalist_at_top_outlet",
  RetailBuyer = "retail_buyer",
  NetWorth = "net_worth",
  HomeValue = "home_value",
  MaxPlatformFollowers = "max_platform_followers",
  CompanyValuation = "company_valuation",
}

export const IMPLEMENTED_VIP_CRITERIA = [
  RedoCriterionKey.HomeValue,
  RedoCriterionKey.MaxPlatformFollowers,
  RedoCriterionKey.InvestorOrWealthManager,
] as const;

interface BaseCriterion {
  displayName: string;
  /**
   * Bumped when the question changes. A verdict stored against an older version
   * is stale: its `reason` explains a question we no longer ask.
   */
  version: number;
}

export interface LlmCriterion extends BaseCriterion {
  evaluator: RecognizeCriterionEvaluator.Llm;
  prompt: string;
}

export interface DeterministicCriterion extends BaseCriterion {
  evaluator: RecognizeCriterionEvaluator.Deterministic;
}

export type RedoCriterion = LlmCriterion | DeterministicCriterion;

/**
 * The default set Redo provisions for every team. Merchants add their own on
 * top; nothing here is hardcoded per team.
 */
export const REDO_DEFAULT_CRITERIA: Record<RedoCriterionKey, RedoCriterion> = {
  [RedoCriterionKey.InvestorOrWealthManager]: {
    evaluator: RecognizeCriterionEvaluator.Llm,
    displayName: "Investor or wealth manager",
    version: 1,
    prompt:
      "Does this person invest or manage money professionally — as an angel or venture investor, a fund manager, a wealth or asset manager, or a comparable role? Their own personal wealth is not relevant; what matters is whether investing or managing money is what they do. Answer true for a partner at a fund, a portfolio manager, a financial advisor managing client assets, or an active angel investor. Answer false for someone who merely holds investments, and for a founder whose only investing is in their own company.",
  },
  [RedoCriterionKey.AssistantToPublicFigure]: {
    evaluator: RecognizeCriterionEvaluator.Llm,
    displayName: "Assistant to a public figure",
    version: 1,
    prompt:
      "Is this person an executive assistant, chief of staff, personal assistant, or similar direct support staff to a widely known public figure or celebrity? The profile must indicate both the support role and that the person they support is genuinely well known to the public. Answer false for an executive assistant at a company whose leadership is not publicly prominent.",
  },
  [RedoCriterionKey.WidelyKnownFigure]: {
    evaluator: RecognizeCriterionEvaluator.Llm,
    displayName: "Widely known figure",
    version: 1,
    prompt:
      "Is this person widely known to the general public, or the holder of a major extraordinary achievement? Evidence that qualifies: a Wikipedia article about them, sustained national or international press coverage, a major award or championship, notable public office, or comparable public recognition. Answer false for someone known only within their industry, someone whose coverage is limited to routine business announcements or press releases, and someone notable only for the company they work at.",
  },
  [RedoCriterionKey.IndustryExpert]: {
    evaluator: RecognizeCriterionEvaluator.Llm,
    displayName: "Renowned industry expert",
    version: 1,
    prompt:
      "Is this person a recognized authority in their field, with public output that establishes it? Evidence that qualifies: a published book, especially a bestseller; keynote or conference speaking; a named academic or research position; frequent citation as an expert by press; or a widely followed body of professional writing or research. Answer false for someone who is simply senior or experienced without such public recognition.",
  },
  [RedoCriterionKey.SeniorExecutive]: {
    evaluator: RecognizeCriterionEvaluator.Llm,
    displayName: "Senior executive",
    version: 1,
    prompt:
      "Does this person hold a senior executive role at their current company? Answer true for founder, owner, C-level (CEO, CFO, CTO, COO and equivalents), president, partner, managing director, and vice president or above at a large organization. Answer false for individual contributors, managers, directors below VP, and a title that sounds senior but describes a small individual practice.",
  },
  [RedoCriterionKey.JournalistAtTopOutlet]: {
    evaluator: RecognizeCriterionEvaluator.Llm,
    displayName: "Journalist at a major outlet",
    version: 1,
    prompt:
      "Is this person a working journalist, editor, or on-air correspondent at a major media outlet? A major outlet means a national newspaper or magazine, a national broadcast or cable network, a major wire service, or a widely read digital publication of comparable reach. Answer true for a staff writer or a regular contributing correspondent. Answer false for a communications or PR professional, a marketing content writer, an independent blogger or newsletter author with no major outlet affiliation, and a journalist at a purely local outlet.",
  },
  [RedoCriterionKey.RetailBuyer]: {
    evaluator: RecognizeCriterionEvaluator.Llm,
    displayName: "Retail buyer",
    version: 1,
    prompt:
      "Does this person buy or select merchandise for a major retailer — a buyer, merchandiser, category or merchandising manager, or head of buying? A major retailer means a national or large regional chain, a major department store, or a large e-commerce marketplace. Answer false for a procurement role that buys supplies rather than merchandise for resale, and for a buyer at a single independent store.",
  },

  [RedoCriterionKey.NetWorth]: {
    evaluator: RecognizeCriterionEvaluator.Deterministic,
    displayName: "Estimated net worth (USD)",
    version: 1,
  },
  [RedoCriterionKey.HomeValue]: {
    evaluator: RecognizeCriterionEvaluator.Deterministic,
    displayName: "High home value",
    version: 1,
  },
  [RedoCriterionKey.MaxPlatformFollowers]: {
    evaluator: RecognizeCriterionEvaluator.Deterministic,
    displayName: "Influencer / large following",
    version: 1,
  },
  [RedoCriterionKey.CompanyValuation]: {
    evaluator: RecognizeCriterionEvaluator.Deterministic,
    displayName: "Company valuation (USD)",
    version: 1,
  },
};

export const DETERMINISTIC_CRITERION_KEYS = Object.values(
  RedoCriterionKey,
).filter(
  (key) =>
    REDO_DEFAULT_CRITERIA[key].evaluator ===
    RecognizeCriterionEvaluator.Deterministic,
);

export function isRedoCriterionKey(key: string): key is RedoCriterionKey {
  return key in REDO_DEFAULT_CRITERIA;
}

export function isDeterministicCriterionKey(
  key: string,
): key is RedoCriterionKey {
  return (
    isRedoCriterionKey(key) &&
    REDO_DEFAULT_CRITERIA[key].evaluator ===
      RecognizeCriterionEvaluator.Deterministic
  );
}

/**
 * Name of the custom field a criterion projects into. The prefix is
 * load-bearing: declarations share one flat per-team namespace, resolved by name.
 */
export function recognizeCustomFieldName(key: string): string {
  return `${RECOGNIZE_FIELD_PREFIX}${key}`;
}

export const RECOGNIZE_FIELD_PREFIX = "recognize_";

export interface RecognizeCriterion {
  id: bigint;
  key: string;
  evaluator: RecognizeCriterionEvaluator;
  managedBy: RecognizeCriterionManagedBy;
  displayName: string | null;
  /** Null for Redo criteria, whose wording lives in the code registry. */
  promptText: string | null;
  criterionVersion: number;
  enabled: boolean;
  declarationId: string | null;
}
